import { EmptyLines } from "../order/empty-lines"
import { ActionIcon, Avatar, Box, Button, Grid, Group, Modal, Select, Stack, Text, Textarea, TextInput, Tooltip } from "@mantine/core"
import { ImageIcon, MinusIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { IAttachmentPlus } from "../../models/Attachment"
import { calcTermDiscount, currencyFormat, lineNetAmount, lineTaxAmount, lineVatAmount, termDiscountSuffix } from "../../utils"
import type { ITermDiscount } from "../../utils"
import { FileField } from "../form/file-field"
import { useVariantPricing } from "../order/use-variant-pricing"
import { OrderLedger } from "../order/order-ledger"
import { ColumnType, Table } from "../table"
import RenderText from "../ui/render-text"
import { Dot } from "../delivery/atoms"
import { VariantSelectorDrawer } from "../shared/variant-selector-drawer"
import { BuyerSalesDynamicAdditionalForm } from "./dynamic-additional-form"
import classes from "./dynamic-form.module.css"

type Props = {
  values?: any
  setFieldValue?: any
  paymentData?: any
  action?: any
  /** Discount rule the order was sent with (the server re-applies it on save). */
  termDiscount?: ITermDiscount | null
  /** Lines priced 0₮: sending is locked by the caller, the lines are marked here (B35 / B59). */
  zeroPriceCount?: number
}

/**
 * Buyer order edit (canvas OrdB_OrderEdit): the lines card, the order summary with
 * notes (B38: the server re-applies the payment-term discount, so the buyer sees the
 * figure before saving) and the attachments card.
 */
export function BuyerSalesDynamicForm({ values, setFieldValue, action: data, termDiscount = null, zeroPriceCount = 0 }: Props) {
  const { t } = useTranslation(["order", "common", "supplierSales"])
  const [edit, setEdit] = React.useState<string>("")
  const [action, setAction] = React.useState<string[]>([])
  const [drawerOpened, setDrawerOpened] = useState(false)

  const priceLines = useMemo(
    () => (values?.lines || []).map((l: any) => ({ variantId: l.variantId, quantity: l.quantity || 1 })),
    [values?.lines],
  )

  // In buyer-sales: senderBusinessId = buyer, supplierId = receiverBusinessId = seller/supplier
  const { priceByVariant } = useVariantPricing(
    values?.supplierId,
    values?.senderBusinessId,
    priceLines,
  )

  useEffect(() => {
    (values?.lines || []).forEach((l: any, i: number) => {
      const fresh = priceByVariant[l.variantId]
      if (fresh != null && fresh !== l.price) {
        setFieldValue(`lines.${i}.price`, fresh)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceByVariant])

  const columns = useHeader({
    t,
    values,
    setFieldValue,
    onClick: (key, item) => {
      if (key === "remove") {
        setFieldValue("lines", [...values.lines.filter((i: any, index: number) => item.index !== index)])
      } else if (key === "edit") {
        setAction(["edit", item])
      }
    },
  })

  const columnsAttachments = useHeaderAttachments({
    t,
    edit,
    setEdit,
    values,
    setFieldValue,
    errors: {},
    onClick: (key, item) => {
      if (key === "remove") {
        setFieldValue("attachments", [...values.attachments.filter((i: any, index: number) => item.index !== index)])
      } else if (key === "edit") {
        setAction(["edit", item])
      }
    },
  })

  // ── Summary (same figures as the create page, supplier-sales/add-dynamic-form.tsx) ──
  const lines: any[] = values?.lines || []
  const totalQty = lines.reduce((s: number, l: any) => s + (Number(l?.quantity) || 0), 0)
  const grossAmount = lines.reduce((s: number, l: any) => s + (Number(l?.price) || 0) * (Number(l?.quantity) || 0), 0)
  const lineDiscount = lines.reduce((s: number, l: any) => {
    const d = l?.discountType === "PERCENTAGE"
      ? ((l?.price || 0) * (l?.quantity || 0) * (l?.discountValue || 0)) / 100
      : (l?.quantity || 0) * (l?.discountValue || 0)
    return s + (d || 0)
  }, 0)
  const orderAmount = lines.reduce((s: number, l: any) => s + lineNetAmount(l), 0)
  const termAmount = calcTermDiscount(orderAmount, termDiscount)
  const vat = lines.reduce((s: number, l: any) => s + lineVatAmount(l), 0)
  const tax = lines.reduce((s: number, l: any) => s + lineTaxAmount(l), 0)
  const grandTotal = orderAmount - termAmount

  const addAttachment = () =>
    setFieldValue("attachments", [
      ...(values?.attachments || []),
      { name: "", unit: "", price: 0, quantity: 0, discountType: "", discountValue: 0, isEdit: true, isNew: true },
    ])

  return (
    <Stack gap="md">
      {/* Lines */}
      <Box className={classes.card} data-flush>
        <Group justify="space-between" wrap="wrap" gap="sm" className={classes.cardHead}>
          <Group gap="sm">
            <Text className={classes.cardTitle}>{t("order:section.orderLines")}</Text>
            {lines.length > 0 && <Box component="span" className={classes.count}>{t("order:section.productCount", { count: lines.length })}</Box>}
          </Group>
          <Group gap="sm">
            <Button variant="default" onClick={() => setAction(["add", values])}>
              {t("order:buyerSales.addProduct")}
            </Button>
            <Button variant="default" onClick={() => setDrawerOpened(true)}>
              {t("order:buyerSales.addPackage")}
            </Button>
          </Group>
        </Group>
        {zeroPriceCount > 0 && (
          <Group gap="xs" wrap="nowrap" className={classes.notice}>
            <Dot tone="amber" />
            <Text fz="var(--nw-fz-body)">{t("order:buyerOrders.edit.zeroPriceBlocked", { count: zeroPriceCount })}</Text>
          </Group>
        )}
        {lines.length === 0 ? (
          <EmptyLines columns={columns.map((c) => c.title)} />
        ) : (
          <Table columns={columns} name="values.lines" dataSource={values?.lines} pagination={false} />
        )}
      </Box>

      {/* Summary + notes (B38) */}
      <Box className={classes.card}>
        <Grid gap="lg">
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Text className={classes.cardTitle} mb="sm">{t("order:buyerSales.orderSummary")}</Text>
            <Stack gap="sm">
              <Textarea
                label={t("order:buyerOrders.edit.partnerNote")}
                placeholder={t("order:buyerOrders.edit.notePlaceholder")}
                value={values?.senderNote ?? ""}
                onChange={(e) => setFieldValue("senderNote", e.currentTarget.value)}
                autosize
                minRows={3}
              />
              <Textarea
                label={t("order:buyerSales.additionalNote")}
                placeholder={t("order:buyerOrders.edit.notePlaceholder")}
                value={values?.senderAdditionalNote ?? ""}
                onChange={(e) => setFieldValue("senderAdditionalNote", e.currentTarget.value)}
                autosize
                minRows={3}
              />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <OrderLedger
              stats={[
                { value: lines.length, label: t("supplierSales:sales.orderCount") },
                { value: totalQty, label: t("supplierSales:sales.totalQuantity") },
              ]}
              headLabel={t("supplierSales:sales.calculation")}
              rows={[
                { label: t("supplierSales:sales.orderTotalAmount"), value: currencyFormat(grossAmount) },
                { label: t("supplierSales:sales.calculatedVat"), value: currencyFormat(vat), muted: true },
                { label: t("supplierSales:sales.calculatedTax"), value: currencyFormat(tax), muted: true },
                { label: t("supplierSales:sales.calculatedDiscount"), value: `-${currencyFormat(lineDiscount)}`, negative: true },
                ...(termAmount > 0
                  ? [{
                    label: `${t("order:buyerSales.termDiscountApplied")}${termDiscountSuffix(termDiscount)}`,
                    value: `-${currencyFormat(termAmount)}`,
                    negative: true,
                  }]
                  : []),
              ]}
              grand={{ label: t("supplierSales:sales.grandTotal"), value: currencyFormat(grandTotal) }}
            />
            <Text className={classes.hint} mt="xs">{t("order:buyerOrders.edit.serverRecalculates")}</Text>
          </Grid.Col>
        </Grid>
      </Box>

      {/* Attachments */}
      <Box className={classes.card} data-flush>
        <Group justify="space-between" wrap="nowrap" className={classes.cardHead}>
          <Box>
            <Text className={classes.cardTitle}>{t("order:buyerSales.addAttachment")}</Text>
            {!(values?.attachments || []).length && <Text className={classes.hint}>{t("order:buyerOrders.edit.noAttachments")}</Text>}
          </Box>
          <Button variant="default" leftSection={<PlusIcon size={16} />} onClick={addAttachment}>
            {t("order:buyerSales.addAttachment")}
          </Button>
        </Group>
        {(values?.attachments || []).length > 0 && (
          <Table columns={columnsAttachments} name="values.attachments" dataSource={values.attachments} pagination={false} />
        )}
      </Box>

      <VariantSelectorDrawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        businessId={values?.supplierId}
        sellerBusinessId={values?.supplierId}
        buyerBusinessId={values?.senderBusinessId}
        orderType="PURCHASE"
        selectedKeys={(values?.lines || []).map((l: any) => `${l.variantId}-${l.warehouseId}`)}
        onAdd={(line) => setFieldValue("lines", [...(values?.lines || []), line])}
        onAddAll={(lines) => setFieldValue("lines", [...(values?.lines || []), ...lines])}
      />
      <Modal
        opened={action[0] === "add" && values?.supplierId}
        onClose={() => {
          setAction([])
        }}
        withCloseButton={false}
        size="900px"
        padding={0}
        centered>
        <BuyerSalesDynamicAdditionalForm values={values} action={action} setFieldValue={setFieldValue} setAction={setAction} />
      </Modal>
    </Stack>
  )
}

const useHeader = ({
  t,
  values,
  setFieldValue,
  onClick,
}: {
  t: any
  values: any
  setFieldValue: any
  onClick: (e: any, e1: any) => void
}): ColumnType<any>[] => [
  {
    title: t("common:table.action"),
    render: (record, index) => (
      <ActionIcon onClick={() => onClick("remove", { ...record, index })} color="gray" variant="light" size={32} aria-label={t("common:actions.delete")}>
        <TrashIcon size={16} />
      </ActionIcon>
    ),
  },
  {
    title: t("order:buyerSales.image"),
    render: (record) => (
      <Avatar size={52} src={record?.image || null} radius="md" className={classes.thumb}>
        <ImageIcon size={18} />
      </Avatar>
    ),
  },
  {
    title: t("order:buyerSales.productService"),
    dataIndex: "nameApp",
    render: (record) => (
      <Box miw={0}>
        <Text fz="var(--nw-fz-body)" fw={600}>{record?.nameApp || record?.nameMon || record?.name || "-"}</Text>
        <Text className={classes.mono}>{record?.skuCode || "-"}</Text>
      </Box>
    ),
  },
  {
    title: t("order:buyerSales.warehouse"),
    dataIndex: "warehouseId",
    render: (record, index) => {
      const options =
        record.inventories?.map((inv: any) => ({
          value: inv.warehouseId,
          label: inv.warehouseName,
        })) ?? []

      const selectedWarehouse = values.lines[index]?.warehouseId
      if (!selectedWarehouse && options.length === 1) {
        setFieldValue(`lines.${index}.warehouseId`, options[0].value)
      }

      return (
        <Box w={180}>
          <Select
            value={values?.lines?.[index]?.warehouseId ?? null}
            onChange={(v) => setFieldValue(`lines.${index}.warehouseId`, v)}
            placeholder={t("order:buyerSales.selectWarehouse")}
            data={options}
            clearable
          />
        </Box>
      )
    },
  },
  {
    title: t("order:buyerSales.unitPrice"),
    dataIndex: "price",
    titleAlign: "right",
    render: (record) =>
      Number(record?.price) > 0 ? (
        <RenderText text={currencyFormat(record?.price || 0)} isAmount />
      ) : (
        // B35: a 0₮ line is marked (amber), never in red; the send button carries the reason.
        <Group gap={6} wrap="nowrap" justify="flex-end">
          <Dot tone="amber" />
          <Text className={classes.mono}>{currencyFormat(0)}</Text>
        </Group>
      ),
  },
  {
    title: t("order:buyerSales.discount"),
    dataIndex: "discountValue",
    titleAlign: "right",
    render: (record) => {
      const count =
        record?.discountType === "PERCENTAGE"
          ? ((record?.price || 0) * (record?.quantity || 0) * record.discountValue) / 100
          : (record?.quantity || 0) * record?.discountValue
      return <RenderText text={currencyFormat(count || 0)} isAmount />
    },
  },
  {
    title: t("order:buyerSales.orderQuantity"),
    dataIndex: "quantity",
    render: (record, index) => {
      const currentQty = Number(record?.quantity) || 0
      return (
        <Group gap={0} wrap="nowrap" className={classes.stepper}>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="-"
            onClick={() => {
              const numVal = currentQty - 1
              if (!numVal || numVal < 0) return
              setFieldValue(`lines.${index}.quantity`, numVal)
            }}
            disabled={currentQty <= 1}>
            <MinusIcon size={14} />
          </ActionIcon>
          <Text className={classes.qty}>{currentQty}</Text>
          <ActionIcon variant="subtle" color="gray" aria-label="+" onClick={() => setFieldValue(`lines.${index}.quantity`, currentQty + 1)}>
            <PlusIcon size={14} />
          </ActionIcon>
        </Group>
      )
    },
  },
  // DSC-001 — VAT extracted from the inclusive price, never added back on top
  // (src/utils/order-amounts.ts, same as supplier-sales/add-dynamic-form.tsx).
  {
    title: t("order:buyerSales.totalVat"),
    dataIndex: "vatPercent",
    titleAlign: "right",
    render: (record) => <RenderText text={currencyFormat(lineVatAmount(record))} isAmount />,
  },
  {
    title: t("order:buyerSales.totalAmount"),
    dataIndex: "name",
    titleAlign: "right",
    render: (record: any) => <Text className={classes.mono} fw={700} ta="right">{currencyFormat(lineNetAmount(record))}</Text>,
  },
]

const useHeaderAttachments = ({
  t,
  edit,
  setEdit,
  values,
  setFieldValue,
  errors,
  onClick,
}: {
  t: any
  edit: any
  setEdit: any
  values: any
  setFieldValue: any
  errors: any
  onClick: (e: any, e1: any) => void
}): ColumnType<IAttachmentPlus & { isEdit: boolean }>[] => [
  {
    title: t("order:buyerSales.action"),
    width: 50,
    render: (record, index) => {
      return (
        <Tooltip label={t("common:actions.delete")}>
          <ActionIcon onClick={() => onClick("remove", { ...record, index })} color="gray" variant="subtle" aria-label={t("common:actions.delete")}>
            <TrashIcon />
          </ActionIcon>
        </Tooltip>
      )
    },
  },
  {
    title: "#",
    width: 30,
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: t("order:buyerSales.fileUrl"),
    dataIndex: "url",
    render: (record, index) => {
      if (record.isEdit) {
        return (
          <FileField
            value={values.attachments?.[index]?.url}
            onChange={(v) => setFieldValue(`attachments.${index}.url`, v)}
            error={errors[`attachments.${index}.url`]}
            placeholder={t("order:buyerSales.fileUrl")}
            label={""}
          />
        )
      } else return <RenderText text={record?.url || "-"} />
    },
  },
  {
    title: t("order:buyerSales.name"),
    dataIndex: "name",
    render: (record, index) => {
      if (record.isEdit) {
        return (
          <TextInput
            value={values?.attachments?.[index]?.name ?? ""}
            onChange={(e) => setFieldValue(`attachments.${index}.name`, e.target.value)}
            placeholder={t("order:buyerSales.name")}
          />
        )
      } else {
        return (
          <Text size="sm" fw={500} c="var(--mantine-color-dark-5)">
            {record?.name || "-"}
          </Text>
        )
      }
    },
  },
  {
    title: t("order:buyerSales.description"),
    dataIndex: "description",
    render: (record, index) => {
      if (record.isEdit) {
        return (
          <TextInput
            value={values?.attachments?.[index]?.description ?? ""}
            onChange={(e) => setFieldValue(`attachments.${index}.description`, e.target.value)}
            placeholder={t("order:buyerSales.description")}
          />
        )
      } else
        return (
          <Text size="sm" fw={500} c="var(--mantine-color-dark-5)">
            {record?.description || "-"}
          </Text>
        )
    },
  },
]
